import { useState } from 'react';
import { Box } from '@mui/material';
import {
  CasButton,
  CasRenderArrayAsRows,
  CasTable,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import Iconify from 'src/components/iconify';
import { IGeneralObjectType } from 'src/compass/site/util';
import {
  CustomerComposeDialog,
  CasRiskManagerSelect,
  CasTagChip,
  CasTagMultiSelect,
  ICasTag,
  CasYesNoSelect,
  useCurrentUserEmployee,
  useFeatureFlags,
} from 'src/compass/application';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import prependAllOption from 'src/compass/shared/util/prependAllOption';

import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import customerSearchFilter from './_internal/customerSearchFilter';
import useServiceOwnerFilter from './_internal/useServiceOwnerFilter';
import CustomerDialog from './CustomerDialog';
import CustomerBatchSendDialog from './CustomerBatchSendDialog';
import { sendMessageColumn } from './RenderSendMessage';
import { RenderRiskManagerName } from './RenderRiskManagerName';
import { RenderLastTouchpoint } from './RenderLastTouchpoint';
import { useTouchpointFrequencyOptions } from './TouchpointFrequencySelect';
import { TOUCHPOINT_ENTITY } from './TouchpointsTab';

const RMC_FILTER = 'employeeId';

// The Inactive filter's Yes / No, named for what each lists.
const SELECTION_OPTIONS = prependAllOption([
  { name: 'Inactive', id: true },
  { name: 'Active', id: false },
]);

export default function CustomersTab() {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);
  const [composeEntityId, setComposeEntityId] = useState<number | null>(null);
  const batchSendDialog = useCasBoolean();
  // The listing's own url + filters, so Batch Send mails exactly the rows these filters match (and
  // Export is offered on the RMC the listing is for).
  const [requestMetadata, setRequestMetadata] = useState<IGeneralObjectType>();
  const withServiceOwnerFilter = useServiceOwnerFilter(RMC_FILTER);

  const {
    data: riskManagerData,
    isLoading,
    isError,
    error,
  } = useRetrieve({ url: 'CurrentUser/RiskManager' });

  // Already loaded by CustomersPage before this tab mounts.
  const { data: currentEmployee } = useCurrentUserEmployee();

  // Send and Batch Send stay hidden until the Show Customer Send feature flag is on for this user
  // (Administration > Configuration > Feature Flags), so they can stay off in Prod until ready.
  // CasTable reads its columns once, at mount - fine here, since the flags are loaded before any
  // page mounts.
  const showSend = !!useFeatureFlags().showCustomerSend;

  // Frequency lookup: { id: Days, name: Description }. Used in the
  // Last Touchpoint cell to surface the Description for entities that have
  // no last-touchpoint date but a "0 Days" (excluded) frequency.
  const { options: freqOptions } = useTouchpointFrequencyOptions({});

  if (isLoading) return <CasLoader />;
  if (isError) return <CasErrorDisplay message={error.message} />;

  // Export is for the user's own customers only: offered while the RMC filter, as the table last
  // requested it (so after Apply, not while staged in the drawer), is the user's own employee. The
  // API refuses any other RMC (EntityTouchpointService.GetExport).
  const canExport =
    !!currentEmployee?.employeeId &&
    requestMetadata?.params?.[RMC_FILTER] === currentEmployee.employeeId;

  // Is Service Owner goes in right after RMC - see useServiceOwnerFilter.
  const table = withServiceOwnerFilter([
    {
      type: 'component',
      name: RMC_FILTER,
      label: 'RMC',
      isPrimaryFilter: true,
      initialValue: riskManagerData?.riskManagerId,
      // Reset restores the current user rather than clearing: the API rejects a
      // null employeeId for BusDev users.
      resetValue: riskManagerData?.riskManagerId,
      component: CasRiskManagerSelect,
      // All = every customer regardless of RMC, so it is main-office only; a branch
      // user is scoped to their own people, and BusDev to themselves. The API applies
      // the same rule (EntityTouchpointService.GetList) rather than trusting this.
      includeAllOption: !!riskManagerData?.isMainOffice && !riskManagerData?.isBusDev,
    },
    {
      ...customerSearchFilter,
      isPrimaryFilter: true,
      autoFocus: true,
    },
    {
      type: 'component',
      name: 'tagIds',
      label: 'Tags',
      component: CasTagMultiSelect,
    },
    {
      type: 'component',
      name: 'overdueOnly',
      label: 'Touchpoint Overdue',
      includeAllOption: true,
      component: CasYesNoSelect,
    },
    {
      type: 'component',
      name: 'lrpHistory',
      label: 'LRP History?',
      includeAllOption: true,
      component: CasYesNoSelect,
    },
    {
      type: 'component',
      name: 'cmeHistory',
      label: 'CME History?',
      includeAllOption: true,
      component: CasYesNoSelect,
    },
    {
      type: 'number',
      name: 'lrpQuoteWithinDays',
      label: 'LRP Quote Click Within (Days)',
    },
    {
      name: 'phone',
      label: 'Phone',
    },
    /*
      Always on the toolbar, last. Starts (and resets) to Active: inactive customers stay out
      of the list unless asked for. All lists active and inactive alike. The other tabs on
      this endpoint send inactive = false themselves (additionalPayload).
    */
    {
      type: 'select',
      name: 'inactive',
      label: 'Selection',
      isPrimaryFilter: true,
      initialValue: false,
      resetValue: false,
      options: SELECTION_OPTIONS,
    },
  ]);

  return (
    <>
      <CasTable
        key={table.key}
        entity={TOUCHPOINT_ENTITY}
        setRequestMetadata={setRequestMetadata}
        includeExport={canExport}
        refreshBtn
        update={reloadTable}
        columns={[
          {
            field: 'name',
            render: (props: ICasRenderCellProps) => (
              <RenderRiskManagerName {...props} onClick={setDialogRowId} />
            ),
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'tags',
            sortable: false,
            render: ({ row }: ICasRenderCellProps) =>
              (row.tags as ICasTag[] | undefined)?.map((tag) => (
                <Box key={tag.id} sx={{ mb: 0.25 }}>
                  <CasTagChip tag={tag} />
                </Box>
              )),
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: (props: ICasRenderCellProps) => (
              <RenderLastTouchpoint {...props} freqOptions={freqOptions} />
            ),
          },
          ...(showSend ? [sendMessageColumn(setComposeEntityId)] : []),
        ]}
        filtersInDrawer
        /*
          All filters live in the Filters drawer. `isPrimaryFilter` ones also stay
          on the toolbar for quick access (they share the drawer's value, so either
          copy stays in sync, and they get no chip since they're already visible).
          Tags is a multi-select matching ANY selected tag.
        */
        /* CasTable appends the full-screen toggle AFTER whatever it is given, which is what puts
           Batch Send immediately to its left. */
        toolbarButtons={
          showSend
            ? [
                <CasButton
                  key="batchSend"
                  startIcon={<Iconify icon="ic:outline-forum" />}
                  onClick={batchSendDialog.setTrue}
                >
                  Batch Send
                </CasButton>,
              ]
            : []
        }
        filters={table.filters}
      />
      {batchSendDialog.value && requestMetadata && (
        <CustomerBatchSendDialog
          onClose={batchSendDialog.setFalse}
          requestMetadata={requestMetadata}
        />
      )}
      {composeEntityId && (
        <CustomerComposeDialog
          entityId={composeEntityId}
          onClose={() => setComposeEntityId(null)}
        />
      )}
      {dialogRowId && (
        <CustomerDialog
          id={dialogRowId}
          onClose={(dataUpdated?: boolean) => {
            setDialogRowId(null);
            if (dataUpdated) setReloadTable(new Date());
          }}
        />
      )}
    </>
  );
}
